import React, {useState, useEffect} from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  InputBase,
  Tooltip,
} from '@material-ui/core';
import {Skeleton} from '@material-ui/lab';
import axios from 'axios';
import {
  ISupplierUserProfile,
  IUser,
  ICOA,
  IcoaTransactions,
} from 'constants/interfaces';
import {formatDate} from 'constants/index';
import {apiUploadFile} from 'constants/Api';
import {faUpload} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import SearchIcon from '@material-ui/icons/Search';

// Lottie imports
import Lottie from 'react-lottie';
import intelligenceLoaderData from '../../../../../../assets/images/IntelligenceLoader.json';

import './ChartOfAccounts.scss';

enum DocStates {
  UPLOADING = 'UPLOADING',
  ANALYSING = 'ANALYSING',
  DONE = 'DONE',
  ERROR = 'ERROR',
  IDLE = 'IDLE',
}

interface ChartOfAccountsProps {
  user: ISupplierUserProfile;
  customerData: IUser | null;
  accounts: ICOA[];
  allTransactions: IcoaTransactions[];
}

function ChartOfAccounts({
  user,
  customerData,
  accounts,
  allTransactions,
}: ChartOfAccountsProps) {
  const [selectedAccount, setSelectedAccount] = useState<ICOA | null>(null);
  const [accountFilter, setAccountFilter] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [docState, setDocState] = useState<DocStates>(DocStates.IDLE);
  const [, setUploadDoc] = useState<boolean>(false);

  // Lottie loader options
  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: intelligenceLoaderData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  // Array of messages to cycle through when analyzing
  const messages = [
    'Analysing your transactions',
    'Verifying chart of accounts',
    'Verifying the Approach',
    'Reading through transactions',
    'Mapping account data',
    'Matching transactions to accounts',
    'Optimizing data accuracy',
    'Cross-referencing entries',
    'Finalizing account insights',
  ];

  const [messageIndex, setMessageIndex] = useState<number>(0);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (docState === DocStates.ANALYSING) {
      interval = setInterval(() => {
        setMessageIndex((prev) => (prev + 1) % messages.length);
      }, 6000);
    } else {
      setMessageIndex(0);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [docState, messages.length]);

  // Handle PDF upload
  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!event.target.files || event.target.files.length === 0) return;
    setUploadDoc(true);
    setDocState(DocStates.UPLOADING);
    try {
      const file = event.target.files[0];
      console.log('Uploading new statement file:', file.name);
      const fileKey = `${new Date().getTime()}_${user.userId}_customer_pdf.pdf`;
      const fileUrl = await apiUploadFile(file, fileKey);
      const fileUrlEncoded = encodeURIComponent(fileUrl);

      // Switch to analyzing
      setDocState(DocStates.ANALYSING);

      // Call the AI endpoint
      await axios.get(
        `http://khuladev.co.za:4010/transactions/openai?customerId=${customerData?.id}&file=${fileUrlEncoded}`,
      );

      // Done analyzing
      setDocState(DocStates.DONE);
      setUploadDoc(false);
    } catch (error) {
      console.error('Error uploading or processing file:', error);
      setDocState(DocStates.ERROR);
    }
  };

  // If still loading data or user not found:
  if (!customerData) {
    return <Skeleton variant="rect" width="100%" height={200} />;
  }

  // Build unique list of account types for filtering
  const uniqueTypes = Array.from(
    new Set(accounts.map((a) => a.account_type)),
  ).sort();
  uniqueTypes.unshift('All');

  // Filter accounts by type and search
  let filteredAccounts =
    accountFilter === 'All'
      ? accounts
      : accounts.filter((a) => a.account_type === accountFilter);

  if (searchQuery.trim() !== '') {
    filteredAccounts = filteredAccounts.filter((a) =>
      a.account_name.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }

  // Transactions for the selected account
  const selectedAccountTxns = selectedAccount
    ? allTransactions.filter((tx) => tx.account_id === selectedAccount._id)
    : [];

  // Filter dropdown change
  const handleFilterChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setAccountFilter(event.target.value);
  };

  return (
    <Box className="chart-of-accounts-container">
      {/* Main header (title) */}
      <Box
        className="coa-header"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Typography variant="subtitle1">Chart of Accounts</Typography>
      </Box>

      {/* Subheader row: Search bar + Filter + Upload */}
      <Box
        className="coa-subheader"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mt={2}
        mb={2}
      >
        {/* Left grouping: search bar + filter */}
        <Box display="flex" alignItems="center">
          <Paper component="form" className="search-paper">
            <SearchIcon className="search-icon" />
            <InputBase
              placeholder="Search Accounts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
              inputProps={{'aria-label': 'search accounts'}}
            />
          </Paper>
          <Box ml={1}>
            <select
              className="custom-filter"
              value={accountFilter}
              onChange={handleFilterChange}
            >
              {uniqueTypes.map((type, index) => (
                <option key={index} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </Box>
        </Box>
        {/* Upload Statement button */}
        <Box>
          <input
            accept="application/pdf"
            style={{display: 'none'}}
            id="upload-pdf"
            type="file"
            onChange={handleFileUpload}
          />
          <label htmlFor="upload-pdf">
            <Button
              variant="contained"
              className="upload-statement-btn"
              startIcon={<FontAwesomeIcon icon={faUpload} />}
              component="span"
            >
              Upload Statement
            </Button>
          </label>
        </Box>
      </Box>

      {/* Grid layout: Left (Accounts) and Right (Transactions) */}
      <Grid container className="coa-grid-container">
        {/* Left: Accounts List */}
        <Grid item xs={12} md={3} className="grid-column">
          <Paper className="coa-paper" variant="outlined">
            <Box className="coa-left-panel">
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Type</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredAccounts.map((account) => (
                      <Tooltip
                        key={account._id}
                        title={account.description}
                        arrow
                      >
                        <TableRow
                          onClick={() => setSelectedAccount(account)}
                          style={{
                            cursor: 'pointer',
                            backgroundColor:
                              selectedAccount?._id === account._id
                                ? 'lightgreen'
                                : 'transparent',
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.backgroundColor =
                              'lightgreen')
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.backgroundColor =
                              selectedAccount?._id === account._id
                                ? 'lightgreen'
                                : 'transparent')
                          }
                        >
                          <TableCell>{account.account_name}</TableCell>
                          <TableCell>{account.account_type}</TableCell>
                        </TableRow>
                      </Tooltip>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Right: Transactions */}
        <Grid item xs={12} md={9} className="grid-column">
          <Paper className="coa-paper" variant="outlined">
            {/* Only show the header if NOT analysing */}
            {docState !== DocStates.ANALYSING && (
              <Box className="header-container">
                <Typography variant="subtitle1">
                  Transactions for {selectedAccount?.account_name || '...'}
                </Typography>
              </Box>
            )}

            {/* When analysing, show the Lottie loader and cycling message */}
            {docState === DocStates.ANALYSING ? (
              <Box className="loader-container">
                <Lottie options={defaultOptions} height={300} width={300} />
                <Typography className="fading-message">
                  {messages[messageIndex]}
                </Typography>
              </Box>
            ) : selectedAccount ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedAccountTxns.map((transaction, index) => (
                      <TableRow key={index}>
                        <TableCell>{formatDate(transaction.date)}</TableCell>
                        <TableCell>{transaction.description}</TableCell>
                        <TableCell>{transaction.amount}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography>Select an account to view transactions</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default ChartOfAccounts;
